import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/common.css';

function RewardRequests() {
  const [user, setUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [responseModal, setResponseModal] = useState({ show: false, request: null, approve: true });
  const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'approved', 'rejected'
  const navigate = useNavigate();

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/parent/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'parent') {
        navigate('/');
        return;
      }
      setUser(parsedUser);
      fetchRequests();
    } catch (error) {
      console.error('사용자 데이터 파싱 오류:', error);
      navigate('/parent/login');
    }
  }, [navigate]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rewards/family-requests');
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('선물 요청 목록 조회 오류:', error);
      alert('선물 요청 목록을 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = (request, approve) => {
    setResponseModal({ show: true, request, approve });
  };

  const submitResponse = async (note = '') => {
    if (!responseModal.request) return;

    try {
      setProcessingId(responseModal.request.id);

      await api.post(`/rewards/${responseModal.request.id}/respond`, {
        approve: responseModal.approve,
        note: note || null
      });

      alert(responseModal.approve ? '선물 요청이 승인되었습니다!' : '선물 요청이 거절되었습니다.');

      // 목록 새로고침
      fetchRequests();
      setResponseModal({ show: false, request: null, approve: true });

    } catch (error) {
      console.error('응답 처리 오류:', error);
      const errorMessage = error.response?.data?.error || '응답 처리 중 오류가 발생했습니다.';
      const errorDetails = error.response?.data?.details ? '\n상세: ' + JSON.stringify(error.response.data.details) : '';
      alert(errorMessage + errorDetails);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return '승인됨';
      case 'rejected': return '거절됨';
      default: return '대기중';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#28a745';
      case 'rejected': return '#dc3545';
      default: return '#ffc107';
    }
  };

  const getFilteredRequests = () => {
    return requests.filter(request => request.status === activeTab);
  };

  const getTabCount = (status) => {
    return requests.filter(request => request.status === status).length;
  };

  if (!user || loading) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* 헤더 */}
      <div className="page-header">
        <Link to="/parent/dashboard" className="back-btn">← 뒤로가기</Link>
        <h1>🎁 선물 요청 관리</h1>
        <button onClick={() => fetchRequests()} className="btn btn-outline btn-sm">
          새로고침
        </button>
      </div>

      {/* 탭 메뉴 */}
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            대기중 ({getTabCount('pending')})
          </button>
          <button
            className={`tab ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
          >
            승인됨 ({getTabCount('approved')})
          </button>
          <button
            className={`tab ${activeTab === 'rejected' ? 'active' : ''}`}
            onClick={() => setActiveTab('rejected')}
          >
            거절됨 ({getTabCount('rejected')})
          </button>
        </div>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="page-content">
        {getFilteredRequests().length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎁</div>
            <h3>{activeTab === 'pending' ? '대기중인 선물 요청이 없습니다' :
                 activeTab === 'approved' ? '승인된 선물이 없습니다' :
                 '거절된 선물 요청이 없습니다'}</h3>
            <p>{activeTab === 'pending' ? '자녀들이 선물을 요청하면 여기에 표시됩니다' :
                activeTab === 'approved' ? '승인한 선물 요청들이 여기에 표시됩니다' :
                '거절한 선물 요청들이 여기에 표시됩니다'}</p>
          </div>
        ) : (
          <div className="requests-list">
            {getFilteredRequests().map((request) => (
              <div key={request.id} className="request-card">
                <div className="request-header">
                  <div className="request-info">
                    <h3>{request.description}</h3>
                    <div className="request-meta">
                      <p className="child-name">👶 {request.child_name}</p>
                      <p className="request-amount">🌰 {request.amount}개 도토리</p>
                    </div>
                  </div>
                  <div className="request-status">
                    <span
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(request.status) }}
                    >
                      {getStatusText(request.status)}
                    </span>
                    <span className="request-date">
                      {formatDate(request.requested_at)}
                    </span>
                  </div>
                </div>

                {request.status === 'pending' && (
                  <div className="request-actions">
                    <button
                      className="btn btn-danger"
                      onClick={() => handleResponse(request, false)}
                      disabled={processingId === request.id}
                    >
                      거절
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleResponse(request, true)}
                      disabled={processingId === request.id}
                    >
                      {processingId === request.id ? '처리중...' : '승인'}
                    </button>
                  </div>
                )}

                {request.status !== 'pending' && request.response_note && (
                  <div className="response-note">
                    <strong>응답 메모:</strong> {request.response_note}
                  </div>
                )}

                {request.status === 'approved' && request.responded_at && (
                  <div className="approval-info">
                    <span className="approval-date">
                      승인일: {formatDate(request.responded_at)}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 응답 모달 */}
      {responseModal.show && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-header">
              <h3>{responseModal.approve ? '선물 요청 승인' : '선물 요청 거절'}</h3>
            </div>
            <div className="modal-body">
              <div className="request-summary">
                <h4>{responseModal.request.description}</h4>
                <p>👶 {responseModal.request.child_name}</p>
                <p>🌰 {responseModal.request.amount}개 도토리</p>
              </div>

              <div className="form-group">
                <label>
                  {responseModal.approve ? '승인 메모 (선택사항)' : '거절 사유'}
                </label>
                <textarea
                  id="response-note"
                  rows="3"
                  placeholder={responseModal.approve ?
                    '잘했어요! 격려 메시지를 남겨주세요.' :
                    '거절 이유를 구체적으로 설명해주세요.'
                  }
                  maxLength="500"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-outline"
                onClick={() => setResponseModal({ show: false, request: null, approve: true })}
              >
                취소
              </button>
              <button
                className={`btn ${responseModal.approve ? 'btn-primary' : 'btn-danger'}`}
                onClick={() => {
                  const note = document.getElementById('response-note').value;
                  submitResponse(note);
                }}
                disabled={processingId}
              >
                {responseModal.approve ? '승인' : '거절'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .page-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding-bottom: 20px;
        }

        .page-header {
          background: white;
          border-bottom: 1px solid #e0e0e0;
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .page-header h1 {
          margin: 0;
          color: #333;
          font-size: 1.5rem;
        }

        .tabs-container {
          background: white;
          border-bottom: 1px solid #e0e0e0;
          padding: 0 20px;
        }

        .tabs {
          display: flex;
          max-width: 800px;
          margin: 0 auto;
        }

        .tab {
          background: none;
          border: none;
          padding: 15px 20px;
          color: #666;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 3px solid transparent;
          transition: all 0.2s;
          font-size: 0.9rem;
        }

        .tab:hover {
          color: #8B4513;
          background: rgba(139, 69, 19, 0.05);
        }

        .tab.active {
          color: #8B4513;
          border-bottom-color: #8B4513;
          background: rgba(139, 69, 19, 0.05);
        }

        .page-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 30px 20px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .empty-icon {
          font-size: 4rem;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          color: #333;
          margin-bottom: 10px;
        }

        .empty-state p {
          color: #666;
        }

        .requests-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .request-card {
          background: white;
          border-radius: 15px;
          padding: 25px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .request-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }

        .request-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
        }

        .request-info h3 {
          color: #333;
          margin: 0 0 10px 0;
          font-size: 1.3rem;
        }

        .request-meta {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .child-name, .request-amount {
          color: #8B4513;
          font-weight: 500;
          margin: 0;
          font-size: 0.9rem;
        }

        .request-status {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 5px;
        }

        .status-badge {
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: bold;
        }

        .request-date {
          color: #666;
          font-size: 0.85rem;
          margin: 0;
        }

        .request-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .request-actions .btn {
          min-width: 80px;
        }

        .response-note {
          background: #f8f9fa;
          padding: 12px;
          border-radius: 8px;
          margin-top: 15px;
          color: #666;
          font-size: 0.9rem;
        }

        .approval-info {
          background: #e8f5e8;
          padding: 10px 12px;
          border-radius: 8px;
          margin-top: 15px;
          border-left: 4px solid #28a745;
        }

        .approval-date {
          color: #28a745;
          font-weight: 500;
          font-size: 0.85rem;
        }

        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #eee;
        }

        .modal-header h3 {
          margin: 0;
          color: #333;
        }

        .modal-body {
          padding: 20px;
        }

        .request-summary {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .request-summary h4 {
          margin: 0 0 5px 0;
          color: #333;
        }

        .request-summary p {
          margin: 2px 0;
          color: #8B4513;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #333;
        }

        .form-group textarea {
          width: 100%;
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          font-family: inherit;
          resize: vertical;
        }

        .form-group textarea:focus {
          outline: none;
          border-color: #8B4513;
          box-shadow: 0 0 0 2px rgba(139, 69, 19, 0.1);
        }

        .modal-actions {
          padding: 20px;
          border-top: 1px solid #eee;
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        @media (max-width: 768px) {
          .tabs {
            overflow-x: auto;
            white-space: nowrap;
          }

          .tab {
            flex-shrink: 0;
            padding: 12px 16px;
            font-size: 0.85rem;
          }

          .request-header {
            flex-direction: column;
            gap: 15px;
          }

          .request-status {
            align-items: flex-start;
          }

          .request-actions {
            justify-content: space-between;
          }

          .modal {
            margin: 10px;
            max-height: calc(100vh - 20px);
          }
        }
      `}</style>
    </div>
  );
}

export default RewardRequests;